import { useMapPlaceholderPositions } from "@/hooks/use-map-placeholder-positions";
import { SheetModeRenderOrders } from "@/modes/sheet-mode/sheet-mode-render-orders";
import {
  MapWaypointsRenderer,
  MapWaypointsRendererRef,
} from "@faro-lotv/app-component-toolbox";
import { GUID } from "@faro-lotv/foundation";
import { IElement } from "@faro-lotv/ielement-types";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";

type ScansExportFallbackMarkersProps = {
  /** Individual scan IElements to render as markers (SiteScape clusters already expanded into children) */
  scans: IElement[];

  /**
   * Maps each marker scan ID to the tree scan/cluster ID used for selection.
   * For SiteScape children this maps to the parent cluster ID.
   */
  markerScanToTreeScanMap: Map<GUID, GUID>;

  /** Currently selected scan IDs in the export store (may include cluster IDs) */
  selectedIds: GUID[];

  /** Currently hovered scan IDs in the export store (may include cluster IDs) */
  hoveredIds: GUID[];

  /** Callback when a marker is clicked, receives the tree scan/cluster ID */
  onMarkerClicked(treeScanId: GUID, event: MouseEvent): void;

  /** Callback when a marker is hovered or unhovered, receives the tree scan/cluster ID */
  onMarkerHovered(treeScanId: GUID | undefined): void;
};

/**
 * @returns Scan markers in the scene for scans that do not have a 360 pano or odometry path
 * (e.g. SiteScape, Photogrammetry scans), using world positions computed from the scan IElements.
 * For SiteScape clusters, individual child scan markers are shown and clicking any child selects the cluster.
 */
export function ScansExportFallbackMarkers({
  scans,
  markerScanToTreeScanMap,
  selectedIds,
  hoveredIds,
  onMarkerClicked,
  onMarkerHovered,
}: ScansExportFallbackMarkersProps): JSX.Element | null {
  const positions = useMapPlaceholderPositions(scans);

  // Build a reverse lookup: tree scan/cluster ID → set of marker indices
  // so that selecting a cluster highlights all its child scan markers
  const treeScanToMarkerIndices = useMemo(() => {
    const map = new Map<GUID, number[]>();
    scans.forEach((scan, index) => {
      const treeScanId = markerScanToTreeScanMap.get(scan.id) ?? scan.id;
      const indices = map.get(treeScanId);
      if (indices) {
        indices.push(index);
      } else {
        map.set(treeScanId, [index]);
      }
    });
    return map;
  }, [scans, markerScanToTreeScanMap]);

  const selectedIndices = useMemo(() => {
    const indices: number[] = [];
    for (const id of selectedIds) {
      const markerIndices = treeScanToMarkerIndices.get(id);
      if (markerIndices) {
        indices.push(...markerIndices);
      }
    }
    return indices;
  }, [selectedIds, treeScanToMarkerIndices]);

  const [rendererRef, setRendererRef] =
    useState<MapWaypointsRendererRef | null>(null);

  // Sync selected indices to the renderer imperatively
  useEffect(() => {
    if (!rendererRef) return;
    rendererRef.selectedIds = selectedIndices;
  }, [rendererRef, selectedIndices]);

  // Use the imperative ref (not the hoveredId prop) to set hovered markers,
  // because the prop would override the renderer's own mouse-based hover state
  // and cause flickering when the hovered marker and the prop point to different indices
  useEffect(() => {
    if (!rendererRef) return;
    const indices: number[] = [];
    for (const id of hoveredIds) {
      const markerIndices = treeScanToMarkerIndices.get(id);
      if (markerIndices) {
        indices.push(...markerIndices);
      }
    }
    rendererRef.hoveredIds = indices;
  }, [rendererRef, hoveredIds, treeScanToMarkerIndices]);

  const handleClick = useCallback(
    (index: number, event: MouseEvent) => {
      const scan = scans[index];
      const treeScanId = markerScanToTreeScanMap.get(scan.id) ?? scan.id;
      onMarkerClicked(treeScanId, event);
    },
    [scans, markerScanToTreeScanMap, onMarkerClicked],
  );

  const handleHovered = useCallback(
    (index?: number) => {
      if (index === undefined) {
        onMarkerHovered(undefined);
      } else {
        const scan = scans[index];
        const treeScanId = markerScanToTreeScanMap.get(scan.id) ?? scan.id;
        onMarkerHovered(treeScanId);
      }
    },
    [scans, markerScanToTreeScanMap, onMarkerHovered],
  );

  if (scans.length === 0) return null;

  return (
    <MapWaypointsRenderer
      ref={setRendererRef}
      waypoints={positions}
      renderOrder={SheetModeRenderOrders.Waypoints}
      onPlaceholderClick={handleClick}
      onPlaceholderHovered={handleHovered}
      generateMipmaps
      channelBlending
    />
  );
}
